import React, { useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { useSyncStatus } from '../contexts/SyncStatusContext';

interface SyncStatusIndicatorProps {
  className?: string;
}

// Temporary type definitions until we implement the real sync system
type SyncDirection = 'disconnected' | 'download' | 'upload' | 'sync';

const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const [isSyncing, setIsSyncing] = useState(false);
  const { state: syncState } = useSyncStatus();

  // Use Convex sync status when connected (authenticated + Convex); otherwise show disconnected
  const isConnected = syncState.isConnected;
  const hasNewerRemoteData = false;
  const hasNewerLocalData = false;
  const lastSyncDate: Date | null = syncState.lastSyncDate;

  // Format last sync time
  const getLastSyncText = () => {
    if (!lastSyncDate) return t('settings.sync.messages.neverSynced');

    const now: Date = new Date();
    const syncDate: Date = lastSyncDate;
    const diff = now.getTime() - syncDate.getTime();

    // Less than a minute ago
    if (diff < 60 * 1000) {
      return t('common.justNow');
    }

    // Less than an hour ago
    if (diff < 60 * 60 * 1000) {
      const minutes = Math.floor(diff / (60 * 1000));
      return t('common.minutesAgo', { count: minutes });
    }

    // Less than a day ago
    if (diff < 24 * 60 * 60 * 1000) {
      const hours = Math.floor(diff / (60 * 60 * 1000));
      return t('common.hoursAgo', { count: hours });
    }

    // More than a day ago
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    return t('common.daysAgo', { count: days });
  };

  // Get the formatted last sync date text with translation
  const getFormattedLastSyncText = () => {
    if (!lastSyncDate) return t('settings.sync.messages.neverSynced');
    return `${t('settings.sync.lastSync', { date: '' })}: ${getLastSyncText()}`;
  };

  // Determine the sync direction
  const getSyncDirection = (): SyncDirection => {
    if (!isConnected) {
      return 'disconnected';
    }

    if (hasNewerRemoteData && !hasNewerLocalData) {
      return 'download';
    } else if (hasNewerLocalData) {
      return 'upload';
    }
    return 'sync';
  };

  const syncDirection = getSyncDirection();

  // Get the appropriate tooltip text based on the sync status
  const getTooltipText = () => {
    if (!isConnected) {
      return t('settings.sync.notConnected');
    }

    if (isSyncing) {
      if (syncDirection === 'download') {
        return t('settings.sync.downloadingFromCloud');
      } else if (syncDirection === 'upload') {
        return t('settings.sync.uploadingToCloud');
      } else {
        return t('settings.sync.syncing');
      }
    }

    if (syncDirection === 'download') {
      return t('settings.sync.downloadNewerData');
    } else if (syncDirection === 'upload') {
      return t('settings.sync.uploadYourChanges');
    }

    return t('settings.sync.checkForChanges');
  };

  // Get animation class based on sync direction
  const getAnimationClass = () => {
    if (!isSyncing) return '';

    if (syncDirection === 'download') {
      return 'animate-spin-counterclockwise';
    } else if (syncDirection === 'upload') {
      return 'animate-spin';
    } else {
      return 'animate-spin';
    }
  };

  // Get color class based on sync status and direction
  const getColorClass = () => {
    // Gray when not connected
    if (!isConnected) {
      return 'text-slate-400 dark:text-slate-500';
    }

    // Always slate when syncing
    if (isSyncing) {
      return 'text-slate-700 dark:text-slate-300';
    }

    // Slate when there's local data to upload
    if (hasNewerLocalData) {
      return 'text-slate-700 dark:text-slate-300';
    }

    // Slate when there's remote data to download
    if (hasNewerRemoteData) {
      return 'text-slate-700 dark:text-slate-300';
    }

    // Match the theme switch icon color when there's no sync action needed
    return 'text-slate-500 dark:text-slate-400';
  };

  // Simulate a sync action (temporary)
  const handleSyncClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isConnected || isSyncing) return;

    setIsSyncing(true);
    // Simulate sync delay
    setTimeout(() => {
      setIsSyncing(false);
    }, 2000);
  };

  return (
    <div className={`relative flex items-center ${className}`}>
      <div className="group">
        <button
          onClick={handleSyncClick}
          disabled={isSyncing}
          className="relative rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-surface-800 dark:hover:text-slate-300"
          title={getTooltipText()}
        >
          {/* Main sync icon with directional animation */}
          <ArrowPathIcon className={`h-6 w-6 ${getAnimationClass()} ${getColorClass()}`} />

          {/* Show exclamation mark when disconnected */}
          {!isConnected && (
            <ExclamationTriangleIcon className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 transform text-slate-400 dark:text-slate-500" />
          )}
        </button>

        <div className="absolute right-0 top-full z-10 mt-1 hidden whitespace-nowrap rounded-md bg-white p-2 text-xs text-slate-600 shadow-lg group-hover:block dark:bg-surface-800 dark:text-slate-300">
          {!isConnected ? (
            <div className="text-slate-500 dark:text-slate-400">
              {t('settings.sync.notConnected')}
              <div className="mt-1 text-xs">{t('common.comingSoon')}</div>
            </div>
          ) : (
            <>
              <div>{getFormattedLastSyncText()}</div>
              {hasNewerRemoteData && !hasNewerLocalData && lastSyncDate && (
                <div className="mt-1 text-slate-600 dark:text-slate-400">
                  {t('settings.sync.newerDataAvailable')}
                </div>
              )}
              {hasNewerLocalData && (
                <div className="mt-1 text-slate-600 dark:text-slate-400">
                  {t('settings.sync.localChangesPending')}
                </div>
              )}
              {!hasNewerRemoteData && !hasNewerLocalData && lastSyncDate && (
                <div className="mt-1 text-slate-500 dark:text-slate-400">
                  {t('settings.sync.upToDate')}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncStatusIndicator;
